<template>
  <div class="gc-bom-bulk-update-step-search">
    <div class="search-page-content">
      <!-- Main column -->
      <div v-show="!showSidebarOnly" class="search-main-col">
        <div class="search-card">
          <div class="search-card-header">
            <span class="search-card-title">Search criteria</span>
          </div>
          <div class="search-card-body">
            <!-- Inline tab bar -->
            <div class="search-tabs">
              <div
                :class="[
                  'search-tab',
                  {
                    active: activeTabIndex === 0,
                    'has-criteria': materialTabActiveCount > 0,
                  },
                ]"
                @click="activeTabIndex = 0"
              >
                Materials
                <span
                  v-if="materialTabActiveCount > 0"
                  class="tab-active-badge"
                  >{{ materialTabActiveCount }}</span
                >
              </div>
              <div
                :class="[
                  'search-tab',
                  {
                    active: activeTabIndex === 1,
                    'has-criteria': processTabActiveCount > 0,
                  },
                ]"
                @click="activeTabIndex = 1"
              >
                Processes
                <span
                  v-if="processTabActiveCount > 0"
                  class="tab-active-badge"
                  >{{ processTabActiveCount }}</span
                >
              </div>
            </div>

            <p class="search-hint-text">
              Activate one or more search types. Types on the same tab are
              combined as a union. Criteria across both tabs are intersected
              (AND).
            </p>

            <!-- ── Materials tab ── -->
            <div v-show="activeTabIndex === 0">
              <!-- Block 1: Material Code(s) -->
              <div
                :class="['stb', { 'stb-on': materialSearch.codeOnly.active }]"
              >
                <div class="stb-hdr">
                  <input
                    type="checkbox"
                    v-model="materialSearch.codeOnly.active"
                    class="stb-chk"
                    id="chk-mat-code"
                  />
                  <label class="stb-lbl" for="chk-mat-code"
                    >Material Code(s)</label
                  >
                </div>
                <div class="stb-desc">
                  BOMs containing any of the nominated material codes are
                  matched.
                </div>
                <div
                  v-if="materialSearch.codeOnly.active"
                  class="stb-body search-mat-code-block"
                >
                  <DxSelectBox
                    ref="codeOnlySelect"
                    :value="codeOnlySelectValue"
                    :data-source="[]"
                    display-expr="Code"
                    value-expr="Code"
                    :search-enabled="true"
                    :search-expr="['Code', 'Description1']"
                    :drop-down-options="{
                      width: 400,
                      wrapperAttr: {
                        class: 'material-search-dropdown',
                      },
                    }"
                    item-template="material-item"
                    placeholder="Type or search..."
                    :show-clear-button="false"
                    :disabled="isReadOnly"
                    @focus-in="onMaterialSelectFocusIn"
                    @value-changed="onMaterialCodeSelected('codeOnly', $event)"
                    :width="'250px'"
                  >
                    <template #material-item="{ data }">
                      <div class="custom-auto-complete">
                        <span class="auto-complete-primary">{{
                          data.Code
                        }}</span>
                        <span class="auto-complete-secondary">{{
                          data.Description1
                        }}</span>
                      </div>
                    </template>
                  </DxSelectBox>
                  <div
                    v-if="materialSearch.codeOnly.codes.length > 0"
                    class="tag-list"
                  >
                    <span
                      v-for="(code, ci) in materialSearch.codeOnly.codes"
                      :key="ci"
                      class="stb-tag"
                    >
                      {{ code }}
                      <span
                        class="stb-tag-x"
                        @click="removeCode('codeOnly', ci)"
                        >×</span
                      >
                    </span>
                  </div>
                  <span
                    v-if="
                      searchAttempted &&
                      materialSearch.codeOnly.codes.length === 0
                    "
                    class="stb-error"
                    >At least one material code is required.</span
                  >
                </div>
              </div>

              <!-- Block 2: Material Qty Range -->
              <div
                :class="['stb', { 'stb-on': materialSearch.qtyRange.active }]"
              >
                <div class="stb-hdr">
                  <input
                    type="checkbox"
                    v-model="materialSearch.qtyRange.active"
                    class="stb-chk"
                    id="chk-mat-qty"
                  />
                  <label class="stb-lbl" for="chk-mat-qty"
                    >Material Qty range</label
                  >
                </div>
                <div class="stb-desc">
                  BOMs with any material line qty within the specified range are
                  matched.
                </div>
                <div v-if="materialSearch.qtyRange.active" class="stb-body">
                  <div class="replacement-header">
                    <div class="rt-col-code">Min qty</div>
                    <div class="rt-col-code">Max qty</div>
                    <div class="rt-col-code">Unit</div>
                    <div class="rt-col-del"></div>
                  </div>
                  <div
                    v-for="(row, index) in materialSearch.qtyRange.rows"
                    :key="index"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxNumberBox
                        :value="row.minQty"
                        @value-changed="row.minQty = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="searchAttempted && row.minQty == null"
                        class="param-error"
                        >Required.</span
                      >
                    </div>
                    <div class="rt-field">
                      <DxNumberBox
                        :value="row.maxQty"
                        @value-changed="row.maxQty = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="searchAttempted && row.maxQty == null"
                        class="param-error"
                        >Required.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          row.minQty != null &&
                          row.maxQty < row.minQty
                        "
                        class="param-error"
                        >Must be ≥ Min qty.</span
                      >
                    </div>
                    <div class="rt-field rt-field-narrow">
                      <DxSelectBox
                        :value="row.uom"
                        :data-source="uomDataSource"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        placeholder="—"
                        :show-clear-button="true"
                        :drop-down-options="{ width: 120 }"
                        item-template="uom-item"
                        @value-changed="row.uom = $event.value"
                      >
                        <template #uom-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span
                        v-if="searchAttempted && !row.uom"
                        class="param-error"
                        >Required.</span
                      >
                    </div>
                    <div class="rt-col-del">
                      <DxButton
                        icon="trash"
                        type="normal"
                        styling-mode="text"
                        class="rt-del-btn"
                        :disabled="materialSearch.qtyRange.rows.length === 1"
                        @click="removeQtyRangeRow(index)"
                      />
                    </div>
                  </div>
                  <DxButton
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addQtyRangeRow"
                  />
                </div>
              </div>

              <!-- Block 3: Material Code(s) and Qty -->
              <div
                :class="['stb', { 'stb-on': materialSearch.codeAndQty.active }]"
              >
                <div class="stb-hdr">
                  <input
                    type="checkbox"
                    v-model="materialSearch.codeAndQty.active"
                    class="stb-chk"
                    id="chk-mat-both"
                  />
                  <label class="stb-lbl" for="chk-mat-both"
                    >Material Code(s) and Qty</label
                  >
                </div>
                <div class="stb-desc">
                  BOMs matched only where nominated code(s) exist with qty
                  within the specified range.
                </div>
                <div v-if="materialSearch.codeAndQty.active" class="stb-body">
                  <DxSelectBox
                    ref="codeAndQtySelect"
                    :value="codeAndQtySelectValue"
                    :data-source="materialCodeDataSource"
                    display-expr="Code"
                    value-expr="Code"
                    :search-enabled="true"
                    :search-expr="['Code', 'Description1']"
                    :drop-down-options="{ width: 400 }"
                    item-template="material-item"
                    placeholder="Type or search..."
                    :show-clear-button="false"
                    :disabled="isReadOnly"
                    style="margin-bottom: 8px"
                    @focus-in="onMaterialSelectFocusIn"
                    @value-changed="
                      onMaterialCodeSelected('codeAndQty', $event)
                    "
                    :width="'250px'"
                  >
                    <template #material-item="{ data }">
                      <div class="custom-auto-complete">
                        <span class="auto-complete-primary">{{
                          data.Code
                        }}</span>
                        <span class="auto-complete-secondary">{{
                          data.Description1
                        }}</span>
                      </div>
                    </template>
                  </DxSelectBox>
                  <div
                    v-if="materialSearch.codeAndQty.codes.length > 0"
                    class="tag-list"
                    style="margin-bottom: 8px"
                  >
                    <span
                      v-for="(code, ci) in materialSearch.codeAndQty.codes"
                      :key="ci"
                      class="stb-tag"
                    >
                      {{ code }}
                      <span
                        class="stb-tag-x"
                        @click="removeCode('codeAndQty', ci)"
                        >×</span
                      >
                    </span>
                  </div>
                  <span
                    v-if="
                      searchAttempted &&
                      materialSearch.codeAndQty.codes.length === 0
                    "
                    class="stb-error"
                    style="display: block; margin-bottom: 6px"
                    >At least one material code is required.</span
                  >
                  <div class="replacement-header">
                    <div class="rt-col-code">Min qty</div>
                    <div class="rt-col-code">Max qty</div>
                    <div class="rt-col-code">Unit</div>
                    <div class="rt-col-del"></div>
                  </div>
                  <div
                    v-for="(row, index) in materialSearch.codeAndQty.rows"
                    :key="index"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxNumberBox
                        :value="row.minQty"
                        @value-changed="row.minQty = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="searchAttempted && row.minQty == null"
                        class="param-error"
                        >Required.</span
                      >
                    </div>
                    <div class="rt-field">
                      <DxNumberBox
                        :value="row.maxQty"
                        @value-changed="row.maxQty = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="searchAttempted && row.maxQty == null"
                        class="param-error"
                        >Required.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          row.minQty != null &&
                          row.maxQty < row.minQty
                        "
                        class="param-error"
                        >Must be ≥ Min qty.</span
                      >
                    </div>
                    <div class="rt-field rt-field-narrow">
                      <DxSelectBox
                        :value="row.uom"
                        :data-source="uomDataSource"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        placeholder="—"
                        :show-clear-button="true"
                        :drop-down-options="{ width: 120 }"
                        item-template="uom-item"
                        @value-changed="row.uom = $event.value"
                      >
                        <template #uom-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span
                        v-if="searchAttempted && !row.uom"
                        class="param-error"
                        >Required.</span
                      >
                    </div>
                    <div class="rt-col-del">
                      <DxButton
                        icon="trash"
                        type="normal"
                        styling-mode="text"
                        class="rt-del-btn"
                        :disabled="materialSearch.codeAndQty.rows.length === 1"
                        @click="removeCodeAndQtyRow(index)"
                      />
                    </div>
                  </div>
                  <DxButton
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addCodeAndQtyRow"
                  />
                </div>
              </div>

              <!-- Block 4: Material Attributes -->
              <div
                :class="[
                  'stb',
                  { 'stb-on': materialSearch.materialAttributes.active },
                ]"
              >
                <div class="stb-hdr">
                  <input
                    type="checkbox"
                    v-model="materialSearch.materialAttributes.active"
                    class="stb-chk"
                    id="chk-mat-attr"
                  />
                  <label class="stb-lbl" for="chk-mat-attr"
                    >Material Attributes</label
                  >
                </div>
                <div class="stb-desc">
                  Matches BOMs containing at least one material whose product
                  attributes match the selected values. Multiple values within a
                  field are combined as OR; multiple fields are combined as AND.
                </div>
                <div
                  v-if="materialSearch.materialAttributes.active"
                  class="stb-body"
                >
                  <div class="attribute-grid">
                    <div class="attribute-field">
                      <label>Product Category 1</label>
                      <DxTagBox
                        :maxDisplayedTags="2"
                        :showMultiTagOnly="false"
                        :value="
                          materialSearch.materialAttributes
                            .productCategory1Codes
                        "
                        :data-source="
                          materialAttributesDataSources.productCategory1
                        "
                        value-expr="Code"
                        :display-expr="
                          (e) => {
                            if (e) return `${e.Code} - ${e.Description}`;
                            return '';
                          }
                        "
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :show-clear-button="true"
                        placeholder="Select..."
                        :disabled="isReadOnly"
                        @value-changed="
                          materialSearch.materialAttributes.productCategory1Codes =
                            $event.value
                        "
                        :item-template="
                          (data) => {
                            return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                          }
                        "
                      >
                      </DxTagBox>
                    </div>

                    <div class="attribute-field">
                      <label>Product Category 2</label>
                      <DxTagBox
                        :value="
                          materialSearch.materialAttributes
                            .productCategory2Codes
                        "
                        :data-source="
                          materialAttributesDataSources.productCategory2
                        "
                        value-expr="Code"
                        :display-expr="
                          (e) => {
                            if (e) return `${e.Code} - ${e.Description}`;
                            return '';
                          }
                        "
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :show-clear-button="true"
                        placeholder="Select..."
                        :disabled="isReadOnly"
                        @value-changed="
                          materialSearch.materialAttributes.productCategory2Codes =
                            $event.value
                        "
                        :item-template="
                          (data) => {
                            return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                          }
                        "
                      >
                      </DxTagBox>
                    </div>

                    <div class="attribute-field">
                      <label>Product Category 3</label>
                      <DxTagBox
                        :value="
                          materialSearch.materialAttributes
                            .productCategory3Codes
                        "
                        :data-source="
                          materialAttributesDataSources.productCategory3
                        "
                        value-expr="Code"
                        :display-expr="
                          (e) => {
                            if (e) return `${e.Code} - ${e.Description}`;
                            return '';
                          }
                        "
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :show-clear-button="true"
                        placeholder="Select..."
                        :disabled="isReadOnly"
                        @value-changed="
                          materialSearch.materialAttributes.productCategory3Codes =
                            $event.value
                        "
                        :item-template="
                          (data) => {
                            return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                          }
                        "
                      >
                      </DxTagBox>
                    </div>

                    <div class="attribute-field">
                      <label>Product Category 4</label>
                      <DxTagBox
                        :value="
                          materialSearch.materialAttributes
                            .productCategory4Codes
                        "
                        :data-source="
                          materialAttributesDataSources.productCategory4
                        "
                        value-expr="Code"
                        :display-expr="
                          (e) => {
                            if (e) return `${e.Code} - ${e.Description}`;
                            return '';
                          }
                        "
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :show-clear-button="true"
                        placeholder="Select..."
                        :disabled="isReadOnly"
                        @value-changed="
                          materialSearch.materialAttributes.productCategory4Codes =
                            $event.value
                        "
                        :item-template="
                          (data) => {
                            return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                          }
                        "
                      >
                      </DxTagBox>
                    </div>

                    <div class="attribute-field">
                      <label>Product Type</label>
                      <DxTagBox
                        :value="
                          materialSearch.materialAttributes.productTypeCodes
                        "
                        :data-source="materialAttributesDataSources.productType"
                        value-expr="Code"
                        :display-expr="
                          (e) => {
                            if (e) return `${e.Code} - ${e.Description}`;
                            return '';
                          }
                        "
                        :search-enabled="true"
                        :search-expr="['Code', 'Description']"
                        :show-clear-button="true"
                        placeholder="Select..."
                        :disabled="isReadOnly"
                        @value-changed="
                          materialSearch.materialAttributes.productTypeCodes =
                            $event.value
                        "
                        :item-template="
                          (data) => {
                            return `<div class='custom-auto-complete'>
                            <span class='auto-complete-primary'>${data.Description}</span>
                            <span class='auto-complete-secondary'>${data.Code}</span>
                          </div>`;
                          }
                        "
                      >
                      </DxTagBox>
                    </div>
                  </div>

                  <span
                    v-if="
                      searchAttempted &&
                      materialSearch.materialAttributes.active &&
                      !isMaterialAttributesValid
                    "
                    class="stb-error"
                  >
                    Please select at least one attribute value to use this
                    search type.
                  </span>
                </div>
              </div>
            </div>

            <!-- ── Processes tab ── -->
            <div v-show="activeTabIndex === 1">
              <!-- Block 1: Process Code(s) -->
              <div
                :class="['stb', { 'stb-on': processSearch.codeOnly.active }]"
              >
                <div class="stb-hdr">
                  <input
                    type="checkbox"
                    v-model="processSearch.codeOnly.active"
                    class="stb-chk"
                    id="chk-proc-code"
                  />
                  <label class="stb-lbl" for="chk-proc-code"
                    >Process Code(s)</label
                  >
                </div>
                <div class="stb-desc">
                  BOMs containing any of the nominated process codes are
                  matched.
                </div>
                <div v-if="processSearch.codeOnly.active" class="stb-body">
                  <DxSelectBox
                    ref="procCodeOnlySelect"
                    :value="procCodeOnlySelectValue"
                    :data-source="processCodeDataSource"
                    display-expr="Code"
                    value-expr="Code"
                    :search-enabled="true"
                    :search-expr="['Code', 'Description']"
                    :drop-down-options="{ width: 400 }"
                    item-template="process-item"
                    placeholder="Type or search..."
                    :show-clear-button="false"
                    :disabled="isReadOnly"
                    @focus-in="onProcessSelectFocusIn"
                    @value-changed="onProcessCodeSelected('codeOnly', $event)"
                    :width="'262px'"
                  >
                    <template #process-item="{ data }">
                      <div class="custom-auto-complete">
                        <span class="auto-complete-primary">{{
                          data.Code
                        }}</span>
                        <span class="auto-complete-secondary">{{
                          data.Description
                        }}</span>
                      </div>
                    </template>
                  </DxSelectBox>
                  <div
                    v-if="processSearch.codeOnly.codes.length > 0"
                    class="tag-list"
                  >
                    <span
                      v-for="(code, ci) in processSearch.codeOnly.codes"
                      :key="ci"
                      class="stb-tag"
                    >
                      {{ code }}
                      <span
                        class="stb-tag-x"
                        @click="removeProcessCode('codeOnly', ci)"
                        >×</span
                      >
                    </span>
                  </div>
                  <span
                    v-if="
                      searchAttempted &&
                      processSearch.codeOnly.codes.length === 0
                    "
                    class="stb-error"
                    >At least one process code is required.</span
                  >
                </div>
              </div>

              <!-- Block 2: Process Run Rate Range -->
              <div
                :class="['stb', { 'stb-on': processSearch.rateRange.active }]"
              >
                <div class="stb-hdr">
                  <input
                    type="checkbox"
                    v-model="processSearch.rateRange.active"
                    class="stb-chk"
                    id="chk-proc-rate"
                  />
                  <label class="stb-lbl" for="chk-proc-rate"
                    >Process Run Rate range</label
                  >
                </div>
                <div class="stb-desc">
                  BOMs with any process line run rate within the specified range
                  are matched.
                </div>
                <div v-if="processSearch.rateRange.active" class="stb-body">
                  <div class="stb-range-row">
                    <div class="stb-range-field">
                      <span class="stb-field-label">Min run rate</span>
                      <DxNumberBox
                        :value="processSearch.rateRange.minRate"
                        :min="0"
                        placeholder="0"
                        @value-changed="
                          processSearch.rateRange.minRate = $event.value
                        "
                        :rtlEnabled="true"
                        :format="'###,###,##0.0000'"
                        :width="'125px'"
                      />
                      <span
                        v-if="
                          searchAttempted &&
                          processSearch.rateRange.minRate == null
                        "
                        class="stb-error"
                        >Required.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          processSearch.rateRange.minRate != null &&
                          processSearch.rateRange.minRate <= 0
                        "
                        class="stb-error"
                        >Must be &gt; 0.</span
                      >
                    </div>
                    <div class="stb-range-field">
                      <span class="stb-field-label">Max run rate</span>
                      <DxNumberBox
                        :value="processSearch.rateRange.maxRate"
                        :min="processSearch.rateRange.minRate || 0"
                        placeholder="—"
                        @value-changed="
                          processSearch.rateRange.maxRate = $event.value
                        "
                        :rtlEnabled="true"
                        :format="'###,###,##0.0000'"
                        :width="'125px'"
                      />
                      <span
                        v-if="
                          searchAttempted &&
                          processSearch.rateRange.maxRate == null
                        "
                        class="stb-error"
                        >Required.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          processSearch.rateRange.maxRate != null &&
                          processSearch.rateRange.maxRate <= 0
                        "
                        class="stb-error"
                        >Must be &gt; 0.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          processSearch.rateRange.minRate != null &&
                          processSearch.rateRange.maxRate != null &&
                          processSearch.rateRange.maxRate <
                            processSearch.rateRange.minRate
                        "
                        class="stb-error"
                        >Must be ≥ Min.</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Block 3: Process Code(s) and Run Rate -->
              <div
                :class="['stb', { 'stb-on': processSearch.codeAndRate.active }]"
              >
                <div class="stb-hdr">
                  <input
                    type="checkbox"
                    v-model="processSearch.codeAndRate.active"
                    class="stb-chk"
                    id="chk-proc-both"
                  />
                  <label class="stb-lbl" for="chk-proc-both"
                    >Process Code(s) and Run Rate</label
                  >
                </div>
                <div class="stb-desc">
                  BOMs matched only where nominated code(s) exist with run rate
                  within the specified range.
                </div>
                <div v-if="processSearch.codeAndRate.active" class="stb-body">
                  <DxSelectBox
                    ref="procCodeAndRateSelect"
                    :value="procCodeAndRateSelectValue"
                    :data-source="processCodeDataSource"
                    display-expr="Code"
                    value-expr="Code"
                    :search-enabled="true"
                    :search-expr="['Code', 'Description']"
                    :drop-down-options="{ width: 400 }"
                    item-template="process-item"
                    placeholder="Type or search..."
                    :show-clear-button="false"
                    :disabled="isReadOnly"
                    style="margin-bottom: 8px"
                    @focus-in="onProcessSelectFocusIn"
                    @value-changed="
                      onProcessCodeSelected('codeAndRate', $event)
                    "
                    :width="'262px'"
                  >
                    <template #process-item="{ data }">
                      <div class="custom-auto-complete">
                        <span class="auto-complete-primary">{{
                          data.Code
                        }}</span>
                        <span class="auto-complete-secondary">{{
                          data.Description
                        }}</span>
                      </div>
                    </template>
                  </DxSelectBox>
                  <div
                    v-if="processSearch.codeAndRate.codes.length > 0"
                    class="tag-list"
                    style="margin-bottom: 8px"
                  >
                    <span
                      v-for="(code, ci) in processSearch.codeAndRate.codes"
                      :key="ci"
                      class="stb-tag"
                    >
                      {{ code }}
                      <span
                        class="stb-tag-x"
                        @click="removeProcessCode('codeAndRate', ci)"
                        >×</span
                      >
                    </span>
                  </div>
                  <span
                    v-if="
                      searchAttempted &&
                      processSearch.codeAndRate.codes.length === 0
                    "
                    class="stb-error"
                    style="display: block; margin-bottom: 6px"
                    >At least one process code is required.</span
                  >
                  <div class="stb-range-row">
                    <div class="stb-range-field">
                      <span class="stb-field-label">Min run rate</span>
                      <DxNumberBox
                        :value="processSearch.codeAndRate.minRate"
                        :min="0"
                        placeholder="0"
                        @value-changed="
                          processSearch.codeAndRate.minRate = $event.value
                        "
                        :rtlEnabled="true"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="
                          searchAttempted &&
                          processSearch.codeAndRate.minRate == null
                        "
                        class="stb-error"
                        >Required.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          processSearch.codeAndRate.minRate != null &&
                          processSearch.codeAndRate.minRate <= 0
                        "
                        class="stb-error"
                        >Must be &gt; 0.</span
                      >
                    </div>
                    <div class="stb-range-field">
                      <span class="stb-field-label">Max run rate</span>
                      <DxNumberBox
                        :value="processSearch.codeAndRate.maxRate"
                        :min="processSearch.codeAndRate.minRate || 0"
                        placeholder="—"
                        @value-changed="
                          processSearch.codeAndRate.maxRate = $event.value
                        "
                        :rtlEnabled="true"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="
                          searchAttempted &&
                          processSearch.codeAndRate.maxRate == null
                        "
                        class="stb-error"
                        >Required.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          processSearch.codeAndRate.maxRate != null &&
                          processSearch.codeAndRate.maxRate <= 0
                        "
                        class="stb-error"
                        >Must be &gt; 0.</span
                      >
                      <span
                        v-else-if="
                          searchAttempted &&
                          processSearch.codeAndRate.minRate != null &&
                          processSearch.codeAndRate.maxRate != null &&
                          processSearch.codeAndRate.maxRate <
                            processSearch.codeAndRate.minRate
                        "
                        class="stb-error"
                        >Must be ≥ Min.</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="search-divider" />

            <!-- Scope filters — UI-04.3 -->
            <div class="scope-section-hdr">
              Additional BOM filters (optional)
            </div>
            <div
              :class="[
                'scope-form-wrap',
                { 'scope-form-readonly': isReadOnly },
              ]"
            >
              <gc-form
                form-key="scope-main"
                :form-items="scopeFormItems"
                :form-options="scopeFormOptions"
                :is-normal="true"
                :data-model="scopeFilters"
                :field-data-changed="onScopeFieldChanged"
                form-location="form-normal"
                :scrolling-enabled="false"
                validation-group="scope-main-form"
                :formMode="baseView.mode"
              />
            </div>

            <!-- Attributes 1–20 (collapsible) -->
            <div
              class="scope-attrs-toggle"
              @click="attributesPanelExpanded = !attributesPanelExpanded"
            >
              <span class="scope-section-hdr">Product Attributes (1–20)</span>
              <span v-if="activeAttributeCount > 0" class="scope-attrs-badge">{{
                activeAttributeCount
              }}</span>
              <span class="scope-attrs-caret">{{
                attributesPanelExpanded ? "▲" : "▼"
              }}</span>
            </div>
            <div v-show="attributesPanelExpanded" class="scope-attrs-grid">
              <div v-for="n in 20" :key="n" class="scope-attr-field">
                <span class="stb-field-label"
                  >Attr {{ String(n).padStart(2, "0") }}</span
                >
                <DxSelectBox
                  :value="
                    attrScopeFilters[
                      'productAttribute' + String(n).padStart(2, '0')
                    ]
                  "
                  :data-source="attrDataSources[n - 1]"
                  value-expr="Code"
                  :display-expr="
                    (e) => {
                      if (e) return `${e.Code} - ${e.Description}`;
                      return '';
                    }
                  "
                  :search-enabled="true"
                  :search-expr="['Code', 'Description']"
                  :show-clear-button="true"
                  placeholder="All"
                  :disabled="isReadOnly"
                  @value-changed="
                    onAttrValueChanged(
                      'productAttribute' + String(n).padStart(2, '0'),
                      $event,
                    )
                  "
                  @selection-changed="
                    onAttrSelectionChanged(
                      'productAttribute' + String(n).padStart(2, '0'),
                      $event,
                    )
                  "
                  :item-template="
                    (data) => {
                      return `<div class='custom-auto-complete'>
                        <span class='auto-complete-primary'>${data.Description}</span>
                        <span class='auto-complete-secondary'>${data.Code}</span>
                      </div>`;
                    }
                  "
                >
                </DxSelectBox>
              </div>
            </div>

            <!-- Search action row -->
            <div class="search-action-row">
              <span
                v-if="searchAttempted && !hasAnyActive"
                class="search-error-hint"
                >Please activate at least one search type.</span
              >
              <DxButton
                text="Search BOMs →"
                type="default"
                styling-mode="contained"
                :disabled="!canSearch || searching || isReadOnly"
                @click="onSearchBtnClick"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Active filters sidebar -->
      <div class="search-filter-col">
        <div :class="['search-card', { 'filters-readonly': showSidebarOnly }]">
          <div class="search-card-header">
            <span class="search-card-title">Active filters</span>
            <DxButton
              v-if="hasActiveFilters && !showSidebarOnly"
              text="Reset"
              type="normal"
              styling-mode="outlined"
              @click="resetFilters"
            />
          </div>
          <div class="search-card-body">
            <template v-if="hasActiveFilters">
              <!-- Material Code(s) chips -->
              <template v-if="materialSearch.codeOnly.codes.length > 0">
                <span class="filter-section-label">Material codes</span>
                <div>
                  <span
                    v-for="(code, ci) in materialSearch.codeOnly.codes"
                    :key="'fc-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span class="chip-x" @click="removeCode('codeOnly', ci)"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <!-- Material Qty Range chips -->
              <template v-if="materialSearch.qtyRange.active">
                <span class="filter-section-label">Material qty range</span>
                <div>
                  <span
                    v-for="(row, index) in materialSearch.qtyRange.rows"
                    :key="index"
                    class="filter-chip"
                  >
                    {{ row.minQty }} – {{ row.maxQty }} {{ row.uom }}
                    <span class="chip-x" @click="removeQtyRangeRow(index)"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <!-- Material Code(s) and Qty chips -->
              <template v-if="materialSearch.codeAndQty.codes.length > 0">
                <span class="filter-section-label"
                  >Material codes (with qty)</span
                >
                <div>
                  <span
                    v-for="(code, ci) in materialSearch.codeAndQty.codes"
                    :key="'fbq-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span class="chip-x" @click="removeCode('codeAndQty', ci)"
                      >×</span
                    >
                  </span>
                  <span
                    v-for="(row, index) in materialSearch.codeAndQty.rows"
                    :key="'fbqr-' + index"
                    class="filter-chip"
                  >
                    {{ row.minQty }} – {{ row.maxQty }} {{ row.uom }}
                    <span class="chip-x" @click="removeCodeAndQtyRow(index)"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <!-- Material Attributes chips -->
              <template
                v-if="
                  materialSearch.materialAttributes.active &&
                  isMaterialAttributesValid
                "
              >
                <span
                  v-if="
                    materialSearch.materialAttributes.productCategory1Codes
                      .length > 0
                  "
                  class="filter-section-label"
                  >Mat. Category 1</span
                >
                <div
                  v-if="
                    materialSearch.materialAttributes.productCategory1Codes
                      .length > 0
                  "
                >
                  <span
                    v-for="(code, ci) in materialSearch.materialAttributes
                      .productCategory1Codes"
                    :key="'fma1-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span
                      class="chip-x"
                      @click="
                        materialSearch.materialAttributes.productCategory1Codes.splice(
                          ci,
                          1,
                        )
                      "
                      >×</span
                    >
                  </span>
                </div>
                <span
                  v-if="
                    materialSearch.materialAttributes.productCategory2Codes
                      .length > 0
                  "
                  class="filter-section-label"
                  >Mat. Category 2</span
                >
                <div
                  v-if="
                    materialSearch.materialAttributes.productCategory2Codes
                      .length > 0
                  "
                >
                  <span
                    v-for="(code, ci) in materialSearch.materialAttributes
                      .productCategory2Codes"
                    :key="'fma2-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span
                      class="chip-x"
                      @click="
                        materialSearch.materialAttributes.productCategory2Codes.splice(
                          ci,
                          1,
                        )
                      "
                      >×</span
                    >
                  </span>
                </div>
                <span
                  v-if="
                    materialSearch.materialAttributes.productCategory3Codes
                      .length > 0
                  "
                  class="filter-section-label"
                  >Mat. Category 3</span
                >
                <div
                  v-if="
                    materialSearch.materialAttributes.productCategory3Codes
                      .length > 0
                  "
                >
                  <span
                    v-for="(code, ci) in materialSearch.materialAttributes
                      .productCategory3Codes"
                    :key="'fma3-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span
                      class="chip-x"
                      @click="
                        materialSearch.materialAttributes.productCategory3Codes.splice(
                          ci,
                          1,
                        )
                      "
                      >×</span
                    >
                  </span>
                </div>
                <span
                  v-if="
                    materialSearch.materialAttributes.productCategory4Codes
                      .length > 0
                  "
                  class="filter-section-label"
                  >Mat. Category 4</span
                >
                <div
                  v-if="
                    materialSearch.materialAttributes.productCategory4Codes
                      .length > 0
                  "
                >
                  <span
                    v-for="(code, ci) in materialSearch.materialAttributes
                      .productCategory4Codes"
                    :key="'fma4-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span
                      class="chip-x"
                      @click="
                        materialSearch.materialAttributes.productCategory4Codes.splice(
                          ci,
                          1,
                        )
                      "
                      >×</span
                    >
                  </span>
                </div>
                <span
                  v-if="
                    materialSearch.materialAttributes.productTypeCodes.length >
                    0
                  "
                  class="filter-section-label"
                  >Mat. Product Type</span
                >
                <div
                  v-if="
                    materialSearch.materialAttributes.productTypeCodes.length >
                    0
                  "
                >
                  <span
                    v-for="(code, ci) in materialSearch.materialAttributes
                      .productTypeCodes"
                    :key="'fmapt-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span
                      class="chip-x"
                      @click="
                        materialSearch.materialAttributes.productTypeCodes.splice(
                          ci,
                          1,
                        )
                      "
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <!-- Process Code(s) chips -->
              <template v-if="processSearch.codeOnly.codes.length > 0">
                <span class="filter-section-label">Process codes</span>
                <div>
                  <span
                    v-for="(code, ci) in processSearch.codeOnly.codes"
                    :key="'fp-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span
                      class="chip-x"
                      @click="removeProcessCode('codeOnly', ci)"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <!-- Process Run Rate Range chip -->
              <template v-if="processSearch.rateRange.active">
                <span class="filter-section-label">Process run rate range</span>
                <div>
                  <span class="filter-chip">
                    {{ processSearch.rateRange.minRate }} –
                    {{ processSearch.rateRange.maxRate }}
                    <span
                      class="chip-x"
                      @click="processSearch.rateRange.active = false"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <!-- Process Code(s) and Rate chips -->
              <template v-if="processSearch.codeAndRate.codes.length > 0">
                <span class="filter-section-label"
                  >Process codes (with rate)</span
                >
                <div>
                  <span
                    v-for="(code, ci) in processSearch.codeAndRate.codes"
                    :key="'fpr-' + ci"
                    class="filter-chip"
                  >
                    {{ code }}
                    <span
                      class="chip-x"
                      @click="removeProcessCode('codeAndRate', ci)"
                      >×</span
                    >
                  </span>
                  <span class="filter-chip">
                    {{ processSearch.codeAndRate.minRate }} –
                    {{ processSearch.codeAndRate.maxRate }}
                    <span
                      class="chip-x"
                      @click="processSearch.codeAndRate.active = false"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <!-- Scope filter chips -->
              <template v-if="scopeFilters.billType != null">
                <span class="filter-section-label">Bill Type</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.billType }}
                    <span class="chip-x" @click="scopeFilters.billType = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template v-if="scopeFilters.bomStatus != null">
                <span class="filter-section-label">BOM Status</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.bomStatus === "A" ? "Active" : "Inactive" }}
                    <span class="chip-x" @click="scopeFilters.bomStatus = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template v-if="scopeFilters.branchCode != null">
                <span class="filter-section-label">Branch</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.branchCode }}
                    <span class="chip-x" @click="scopeFilters.branchCode = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template v-if="scopeFilters.divisionCode != null">
                <span class="filter-section-label">Division</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.divisionCode }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.divisionCode = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template v-if="scopeFilters.productionUM != null">
                <span class="filter-section-label">Production UM</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.productionUM }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productionUM = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template v-if="scopeFilters.conversionFactor != null">
                <span class="filter-section-label">Conv. Factor</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.conversionFactor }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.conversionFactor = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template v-if="scopeFilters.productType != null">
                <span class="filter-section-label">Product Type</span>
                <div>
                  <span class="filter-chip">
                    {{ scopeFilters.productType }}
                    <span
                      class="chip-x"
                      @click="scopeFilters.productType = null"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template
                v-if="
                  scopeFilters.productCategory1 != null ||
                  scopeFilters.productCategory2 != null ||
                  scopeFilters.productCategory3 != null ||
                  scopeFilters.productCategory4 != null
                "
              >
                <span class="filter-section-label">Categories</span>
                <div>
                  <span
                    v-if="scopeFilters.productCategory1 != null"
                    key="fcat-1"
                    class="filter-chip"
                  >
                    Cat 1: {{ scopeFilters.productCategory1 }}
                    <span
                      class="chip-x"
                      @click="clearScopeFilter('productCategory1')"
                      >×</span
                    >
                  </span>
                  <span
                    v-if="scopeFilters.productCategory2 != null"
                    key="fcat-2"
                    class="filter-chip"
                  >
                    Cat 2: {{ scopeFilters.productCategory2 }}
                    <span
                      class="chip-x"
                      @click="clearScopeFilter('productCategory2')"
                      >×</span
                    >
                  </span>
                  <span
                    v-if="scopeFilters.productCategory3 != null"
                    key="fcat-3"
                    class="filter-chip"
                  >
                    Cat 3: {{ scopeFilters.productCategory3 }}
                    <span
                      class="chip-x"
                      @click="clearScopeFilter('productCategory3')"
                      >×</span
                    >
                  </span>
                  <span
                    v-if="scopeFilters.productCategory4 != null"
                    key="fcat-4"
                    class="filter-chip"
                  >
                    Cat 4: {{ scopeFilters.productCategory4 }}
                    <span
                      class="chip-x"
                      @click="clearScopeFilter('productCategory4')"
                      >×</span
                    >
                  </span>
                </div>
              </template>
              <template v-if="activeAttributeCount > 0">
                <span class="filter-section-label">Attributes</span>
                <div>
                  <template v-for="n in 20">
                    <span
                      v-if="
                        attrScopeFilters[
                          'productAttribute' + String(n).padStart(2, '0')
                        ] != null
                      "
                      :key="'fa-' + n"
                      class="filter-chip"
                    >
                      Attr {{ String(n).padStart(2, "0") }}:
                      {{
                        attrScopeFilters[
                          "productAttribute" + String(n).padStart(2, "0")
                        ]
                      }}
                      <span
                        class="chip-x"
                        @click="
                          clearScopeFilter(
                            'productAttribute' + String(n).padStart(2, '0'),
                          )
                        "
                        >×</span
                      >
                    </span>
                  </template>
                </div>
              </template>
            </template>
            <span v-else class="search-no-filters">No filters applied</span>
          </div>
        </div>

        <!-- Configure tip — shown only in sidebar mode (step 2) -->
        <div v-if="showSidebarOnly" class="search-card cfg-tip-card">
          <div class="search-card-header">
            <span class="search-card-title">Configure tip</span>
          </div>
          <div class="search-card-body cfg-tip-body">
            Each panel corresponds to the search criteria defined in Step 1.
            Select one operation per panel — both must be valid before
            proceeding to Preview.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxSelectBox from "devextreme-vue/select-box";
import DxTagBox from "devextreme-vue/tag-box";
import DxButton from "devextreme-vue/button";
import ApiService from "@/_services/api/api.service";
import GcForm from "@/components/gc-form";
import { BomBulkUpdateService } from "@/_services/api/production/bom-bulk-update.service";

export default {
  name: "GcBomBulkUpdateStepSearch",
  components: { DxSelectBox, DxTagBox, DxButton, GcForm },
  props: {
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    baseView: {
      type: Object,
      required: true,
    },
    savedCriteria: {
      type: Object,
      default: null,
    },
    showLoading: {
      type: Function,
      required: true,
    },
    showSidebarOnly: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    this.materialCodeDataSource = ApiService.createDataSource("api/Product", {
      select: ["Code", "Description1", "Inactive", "Status"],
      filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
    });
    this.uomDataSource = ApiService.createDataSource("api/UOM", {
      select: ["Code", "IsoCode", "Description", "Inactive"],
      filter: ["Inactive", "=", "N"],
    });
    this.productionUMDataSource = ApiService.createDataSource(
      "api/ProductionUnitOfMeasure",
      {
        select: ["Code", "RecordId", "Description", "Inactive"],
      },
    );

    if (this.savedCriteria) {
      this.updateOnSavedCriteria(this.savedCriteria);
    }
  },
  data() {
    return {
      activeTabIndex: 0,
      materialCodeDataSource: null,
      processCodeDataSource: null,
      uomDataSource: null,
      productionUMDataSource: null,

      scopeFormOptions: { gcLabelLocation: "top" },
      attributesPanelExpanded: false,

      // Pending select values — reset to null after each selection
      codeOnlySelectValue: null,
      codeAndQtySelectValue: null,
      procCodeOnlySelectValue: null,
      procCodeAndRateSelectValue: null,

      // Material search types — each independently toggleable
      materialSearch: {
        codeOnly: { active: false, codes: [] },
        qtyRange: {
          active: false,
          rows: [{ minQty: null, maxQty: null, uom: null }],
        },
        codeAndQty: {
          active: false,
          codes: [],
          rows: [{ minQty: null, maxQty: null, uom: null }],
        },
        materialAttributes: {
          active: false,
          productCategory1Codes: [],
          productCategory2Codes: [],
          productCategory3Codes: [],
          productCategory4Codes: [],
          productTypeCodes: [],
        },
      },

      // Process search types — UI-05.1
      processSearch: {
        codeOnly: { active: false, codes: [] },
        rateRange: { active: false, minRate: null, maxRate: null },
        codeAndRate: {
          active: false,
          codes: [],
          minRate: null,
          maxRate: null,
        },
      },

      // Shared scope filters — UI-04.3 (flat; built into nested payload in buildSearchCriteria)
      scopeFilters: {
        billType: null,
        bomStatus: null,
        branchCode: null,
        divisionCode: null,
        productionUM: null,
        conversionFactor: null,
        productType: null,
        productCategory1: null,
        productCategory2: null,
        productCategory3: null,
        productCategory4: null,
      },

      attrScopeFilters: (() => {
        const obj = {};
        for (let i = 1; i <= 20; i++)
          obj[`productAttribute${String(i).padStart(2, "0")}`] = null;
        return obj;
      })(),
      attrGuidMap: (() => {
        const obj = {};
        for (let i = 1; i <= 20; i++)
          obj[`productAttribute${String(i).padStart(2, "0")}`] = null;
        return obj;
      })(),

      searching: false,
      searchAttempted: false,
      initialRestoreDone: false,
      lastSearchedSnapshot: null,
    };
  },
  watch: {
    savedCriteria(newVal) {
      if (this.initialRestoreDone) return;
      this.updateOnSavedCriteria(newVal);
    },
    hasChangedSinceSearch(val) {
      this.$emit("criteria-change", { isStale: val });
    },
    "materialSearch.codeOnly.active"(newVal) {
      if (!newVal) {
        this.materialSearch.codeOnly.codes = [];
      }
    },
    "materialSearch.qtyRange.active"(newVal) {
      if (!newVal) {
        this.materialSearch.qtyRange.rows = [
          { minQty: null, maxQty: null, uom: null },
        ];
      }
    },
    "materialSearch.codeAndQty.active"(newVal) {
      if (!newVal) {
        this.materialSearch.codeAndQty.codes = [];
        this.materialSearch.codeAndQty.rows = [
          { minQty: null, maxQty: null, uom: null },
        ];
      }
    },
    "processSearch.codeOnly.active"(newVal) {
      if (!newVal) {
        this.processSearch.codeOnly.codes = [];
      }
    },
    "processSearch.rateRange.active"(newVal) {
      if (!newVal) {
        this.processSearch.rateRange.minRate = null;
        this.processSearch.rateRange.maxRate = null;
      }
    },
    "processSearch.codeAndRate.active"(newVal) {
      if (!newVal) {
        this.processSearch.codeAndRate.codes = [];
        this.processSearch.codeAndRate.minRate = null;
        this.processSearch.codeAndRate.maxRate = null;
      }
    },
    "materialSearch.materialAttributes.active"(newVal) {
      if (!newVal) {
        this.materialSearch.materialAttributes.productCategory1Codes = [];
        this.materialSearch.materialAttributes.productCategory2Codes = [];
        this.materialSearch.materialAttributes.productCategory3Codes = [];
        this.materialSearch.materialAttributes.productCategory4Codes = [];
        this.materialSearch.materialAttributes.productTypeCodes = [];
      }
    },
  },
  computed: {
    hasChangedSinceSearch() {
      if (!this.lastSearchedSnapshot) return false;
      return this.buildCriteriaSnapshot() !== this.lastSearchedSnapshot;
    },
    canSearch() {
      return this.isMaterialSearchValid || this.isProcessSearchValid;
    },
    materialTabActiveCount() {
      return this.getActiveMaterialSearchTypes().length;
    },
    processTabActiveCount() {
      return this.getActiveProcessSearchTypes().length;
    },
    isMaterialSearchValid() {
      const m = this.materialSearch;
      if (m.codeOnly.active && m.codeOnly.codes.length > 0) return true;
      if (m.qtyRange.active && this.hasValidRow(m.qtyRange.rows)) return true;
      if (
        m.codeAndQty.active &&
        m.codeAndQty.codes.length > 0 &&
        this.hasValidRow(m.codeAndQty.rows)
      )
        return true;
      if (m.materialAttributes.active && this.isMaterialAttributesValid)
        return true;
      return false;
    },
    isProcessSearchValid() {
      const p = this.processSearch;
      if (p.codeOnly.active && p.codeOnly.codes.length > 0) return true;
      if (p.rateRange.active && this.isValidRateRange(p.rateRange)) return true;
      if (
        p.codeAndRate.active &&
        p.codeAndRate.codes.length > 0 &&
        this.isValidRateRange(p.codeAndRate)
      )
        return true;
      return false;
    },
    isMaterialAttributesValid() {
      var a = this.materialSearch.materialAttributes;
      return (
        (a.productCategory1Codes && a.productCategory1Codes.length > 0) ||
        (a.productCategory2Codes && a.productCategory2Codes.length > 0) ||
        (a.productCategory3Codes && a.productCategory3Codes.length > 0) ||
        (a.productCategory4Codes && a.productCategory4Codes.length > 0) ||
        (a.productTypeCodes && a.productTypeCodes.length > 0)
      );
    },
    hasAnyActive() {
      const m = this.materialSearch;
      const p = this.processSearch;
      return (
        m.codeOnly.active ||
        m.qtyRange.active ||
        m.codeAndQty.active ||
        m.materialAttributes.active ||
        p.codeOnly.active ||
        p.rateRange.active ||
        p.codeAndRate.active
      );
    },
    activeAttributeCount() {
      return Object.values(this.attrScopeFilters).filter((v) => v != null)
        .length;
    },
    attrDataSources() {
      return Array.from({ length: 20 }, (_, i) => {
        const num = String(i + 1).padStart(2, "0");
        return ApiService.createDataSource(`api/ProductAttribute${num}`, {
          select: ["RecordId", "Code", "Description"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
        });
      });
    },
    materialAttributesDataSources() {
      return {
        productCategory1: ApiService.createDataSource("api/ProductCategory1", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productCategory2: ApiService.createDataSource("api/ProductCategory2", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productCategory3: ApiService.createDataSource("api/ProductCategory3", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productCategory4: ApiService.createDataSource("api/ProductCategory4", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
        productType: ApiService.createDataSource("api/ProductType", {
          select: ["RecordId", "Code", "Description", "Inactive"],
          sort: "Code asc",
          key: ["RecordId"],
          keyType: { RecordId: "Guid" },
          fieldTypes: { RecordId: "Guid" },
          filter: ["Inactive", "=", "N"],
        }),
      };
    },
    hasActiveFilters() {
      const s = this.scopeFilters;
      return (
        this.materialSearch.codeOnly.codes.length > 0 ||
        this.materialSearch.qtyRange.active ||
        this.materialSearch.codeAndQty.active ||
        this.materialSearch.materialAttributes.active ||
        this.processSearch.codeOnly.codes.length > 0 ||
        this.processSearch.rateRange.active ||
        this.processSearch.codeAndRate.active ||
        s.billType != null ||
        s.bomStatus != null ||
        s.branchCode != null ||
        s.divisionCode != null ||
        s.productionUM != null ||
        s.conversionFactor != null ||
        s.productType != null ||
        s.productCategory1 != null ||
        s.productCategory2 != null ||
        s.productCategory3 != null ||
        s.productCategory4 != null ||
        this.activeAttributeCount > 0
      );
    },
    scopeFormItems() {
      return [
        {
          itemType: "group",
          colCount: 4,
          items: [
            {
              screenField: "BillType",
              dataField: "billType",
              editorOptions: { placeholder: "All", showClearButton: true },
            },
            {
              dataField: "bomStatus",
              label: { text: "BOM Status" },
              editorType: "dxSelectBox",
              editorOptions: {
                dataSource: [
                  { value: "A", label: "Active" },
                  { value: "X", label: "Inactive" },
                ],
                displayExpr: "label",
                valueExpr: "value",
                showClearButton: true,
                placeholder: "All",
              },
            },
            {
              screenField: "Branch",
              label: { text: "Branch" },
              dataField: "branchCode",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "Division",
              label: { text: "Division" },
              dataField: "divisionCode",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              dataField: "productionUM",
              label: { text: "Production UM" },
              editorType: "dxSelectBox",
              editorOptions: {
                dataSource: this.productionUMDataSource,
                valueExpr: "Code",
                searchEnabled: true,
                placeholder: "All",
                showClearButton: true,
                itemTemplate: (data) => {
                  return ` <div class="custom-auto-complete" id="${data.Code}" >
                            <span class="auto-complete-primary">
                              ${data.Description}
                            </span>
                            <span class="record-secondary gc-grid-code">
                              ${data.Code}
                            </span>
                          </div>`;
                },
                displayExpr: (e) => {
                  if (e) return `${e.Code} - ${e.Description}`;
                  else undefined;
                },
                searchExpr: ["Code", "Description"],
              },
            },
            {
              dataField: "conversionFactor",
              label: { text: "Conversion Factor" },
              editorType: "dxNumberBox",
              editorOptions: {
                min: 0,
                showClearButton: true,
                format: "###,###,##0.0000",
              },
            },
            {
              screenField: "ProductCategory1",
              dataField: "productCategory1",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductCategory2",
              dataField: "productCategory2",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductCategory3",
              dataField: "productCategory3",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductCategory4",
              dataField: "productCategory4",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
            {
              screenField: "ProductType",
              dataField: "productType",
              editorOptions: { placeholder: "All", showClearButton: true },
              displayExpr: (e) => {
                if (e) return `${e.Code} - ${e.Description}`;
                else undefined;
              },
              searchExpr: ["Code", "Description"],
            },
          ],
        },
      ];
    },
  },
  methods: {
    onSearchBtnClick() {
      if (this.lastSearchedSnapshot && !this.hasChangedSinceSearch) {
        this.$emit("go-next");
        return;
      }
      this.executeSearch();
    },
    buildCriteriaSnapshot() {
      return JSON.stringify(this.buildSearchPayload().SearchCriteria);
    },
    updateOnSavedCriteria(newVal) {
      const matCriteria =
        (newVal && (newVal.MaterialCriteria || newVal.materialCriteria)) || [];
      const procCriteria =
        (newVal && (newVal.ProcessCriteria || newVal.processCriteria)) || [];
      if (matCriteria.length > 0 || procCriteria.length > 0) {
        this.restoreFromCriteria(newVal);
        this.initialRestoreDone = true;
      }
    },

    // ─── Tag helpers ───────────────────────────────────────────────────────────

    onMaterialCodeSelected(type, e) {
      if (!e.value) return;
      if (!this.materialSearch[type].codes.includes(e.value)) {
        this.materialSearch[type].codes.push(e.value);
      }
      const selectKey =
        type === "codeOnly" ? "codeOnlySelectValue" : "codeAndQtySelectValue";
      const refName =
        type === "codeOnly" ? "codeOnlySelect" : "codeAndQtySelect";
      this.$nextTick(() => {
        this[selectKey] = null;
        if (this.$refs[refName]) {
          this.$refs[refName].instance.option("value", null);
          this.$refs[refName].instance.focus();
        }
      });
    },
    onMaterialSelectFocusIn(e) {
      e.component.beginUpdate();
      e.component.option("dataSource", null);
      e.component.endUpdate();

      this.$nextTick(() => {
        e.component.beginUpdate();
        e.component.option(
          "dataSource",
          ApiService.createDataSource("api/Product", {
            select: ["Code", "Description1", "Inactive", "Status"],
            filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
          }),
        );
        e.component.endUpdate();
      });
    },
    removeCode(type, index) {
      this.materialSearch[type].codes.splice(index, 1);
    },

    // ─── Process tag helpers ──────────────────────────────────────────────────

    onProcessCodeSelected(type, e) {
      if (!e.value) return;
      if (!this.processSearch[type].codes.includes(e.value)) {
        this.processSearch[type].codes.push(e.value);
      }
      const selectKey =
        type === "codeOnly"
          ? "procCodeOnlySelectValue"
          : "procCodeAndRateSelectValue";
      const refName =
        type === "codeOnly" ? "procCodeOnlySelect" : "procCodeAndRateSelect";
      this.$nextTick(() => {
        this[selectKey] = null;
        if (this.$refs[refName]) {
          this.$refs[refName].instance.option("value", null);
          this.$refs[refName].instance.focus();
        }
      });
    },
    onProcessSelectFocusIn(e) {
      e.component.beginUpdate();
      e.component.option("dataSource", null);
      e.component.endUpdate();

      this.$nextTick(() => {
        e.component.beginUpdate();
        e.component.option(
          "dataSource",
          ApiService.createDataSource("api/Process", {
            select: ["Code", "Description", "Inactive"],
            sort: "Code asc",
            filter: ["Inactive", "=", "N"],
          }),
        );
        e.component.endUpdate();
      });
    },
    removeProcessCode(type, index) {
      this.processSearch[type].codes.splice(index, 1);
    },

    // ─── Range row management ─────────────────────────────────────────────────

    addQtyRangeRow() {
      this.materialSearch.qtyRange.rows.push({
        minQty: null,
        maxQty: null,
        uom: null,
      });
    },
    removeQtyRangeRow(index) {
      if (this.materialSearch.qtyRange.rows.length > 1) {
        this.materialSearch.qtyRange.rows.splice(index, 1);
      }
    },
    addCodeAndQtyRow() {
      this.materialSearch.codeAndQty.rows.push({
        minQty: null,
        maxQty: null,
        uom: null,
      });
    },
    removeCodeAndQtyRow(index) {
      if (this.materialSearch.codeAndQty.rows.length > 1) {
        this.materialSearch.codeAndQty.rows.splice(index, 1);
      }
    },

    // ─── Scope filter helpers ──────────────────────────────────────────────────

    onScopeFieldChanged(e) {
      this.scopeFilters[e.dataField] = e.value !== undefined ? e.value : null;
    },
    onAttrValueChanged(key, e) {
      this.attrScopeFilters[key] = e.value !== undefined ? e.value : null;
    },
    onAttrSelectionChanged(key, e) {
      this.attrGuidMap[key] = e.selectedItem ? e.selectedItem.RecordId : null;
    },
    clearScopeFilter(key) {
      if (key.startsWith("productAttribute")) {
        this.attrScopeFilters[key] = null;
        this.attrGuidMap[key] = null;
      } else {
        this.scopeFilters[key] = null;
      }
    },

    // ─── Validation helpers ────────────────────────────────────────────────────

    isValidRangeRow(row) {
      return (
        row.minQty !== null &&
        row.minQty !== "" &&
        row.maxQty !== null &&
        row.maxQty !== "" &&
        Number(row.minQty) <= Number(row.maxQty) &&
        row.uom
      );
    },
    hasValidRow(rows) {
      var self = this;
      return rows.some(function (row) {
        return self.isValidRangeRow(row);
      });
    },
    isValidRateRange(r) {
      return (
        r.minRate != null &&
        r.maxRate != null &&
        r.minRate > 0 &&
        r.maxRate > 0 &&
        r.minRate <= r.maxRate
      );
    },
    getActiveMaterialSearchTypes() {
      const m = this.materialSearch;
      return [
        { key: 'codeOnly', active: m.codeOnly.active },
        { key: 'qtyRange', active: m.qtyRange.active },
        { key: 'codeAndQty', active: m.codeAndQty.active },
        { key: 'materialAttributes', active: m.materialAttributes.active },
      ].filter(t => t.active).map(t => t.key);
    },
    getActiveProcessSearchTypes() {
      const p = this.processSearch;
      return [
        { key: 'codeOnly', active: p.codeOnly.active },
        { key: 'rateRange', active: p.rateRange.active },
        { key: 'codeAndRate', active: p.codeAndRate.active },
      ].filter(t => t.active).map(t => t.key);
    },
    collectSearchValidationErrors() {
      const errors = [];
      const m = this.materialSearch;
      const p = this.processSearch;
      if (!this.hasAnyActive) {
        errors.push("At least one search type must be active.");
        return errors;
      }

      // Material Code(s)
      if (m.codeOnly.active && m.codeOnly.codes.length === 0)
        errors.push(
          "Material Code(s): Please select at least one material code.",
        );

      // Material Qty Range
      if (m.qtyRange.active) {
        if (!this.hasValidRow(m.qtyRange.rows)) {
          errors.push(
            "Material Qty Range: At least one valid range is required.",
          );
        }
      }

      // Material Code(s) + Qty
      if (m.codeAndQty.active) {
        if (m.codeAndQty.codes.length === 0)
          errors.push(
            "Material Code(s) + Qty: Please select at least one material code.",
          );
        if (!this.hasValidRow(m.codeAndQty.rows)) {
          errors.push(
            "Material Code(s) + Qty: At least one valid range is required.",
          );
        }
      }

      // Material Attributes
      if (m.materialAttributes.active && !this.isMaterialAttributesValid)
        errors.push(
          "Material Attributes: Please select at least one attribute.",
        );

      // Process Code(s)
      if (p.codeOnly.active && p.codeOnly.codes.length === 0)
        errors.push(
          "Process Code(s): Please select at least one process code.",
        );

      // Process Run Rate Range
      if (p.rateRange.active) {
        if (p.rateRange.minRate == null)
          errors.push("Process Run Rate Range: Min Rate is required.");
        else if (p.rateRange.minRate <= 0)
          errors.push(
            "Process Run Rate Range: Min Rate must be greater than 0.",
          );
        if (p.rateRange.maxRate == null)
          errors.push("Process Run Rate Range: Max Rate is required.");
        else if (p.rateRange.maxRate <= 0)
          errors.push(
            "Process Run Rate Range: Max Rate must be greater than 0.",
          );
        else if (
          p.rateRange.minRate != null &&
          p.rateRange.maxRate < p.rateRange.minRate
        )
          errors.push(
            "Process Run Rate Range: Max Rate must be greater than or equal to Min Rate.",
          );
      }

      // Process Code(s) + Run Rate
      if (p.codeAndRate.active) {
        if (p.codeAndRate.codes.length === 0)
          errors.push(
            "Process Code(s) + Run Rate: Please select at least one process code.",
          );
        if (p.codeAndRate.minRate == null)
          errors.push("Process Code(s) + Run Rate: Min Rate is required.");
        else if (p.codeAndRate.minRate <= 0)
          errors.push(
            "Process Code(s) + Run Rate: Min Rate must be greater than 0.",
          );
        if (p.codeAndRate.maxRate == null)
          errors.push("Process Code(s) + Run Rate: Max Rate is required.");
        else if (p.codeAndRate.maxRate <= 0)
          errors.push(
            "Process Code(s) + Run Rate: Max Rate must be greater than 0.",
          );
        else if (
          p.codeAndRate.minRate != null &&
          p.codeAndRate.maxRate < p.codeAndRate.minRate
        )
          errors.push(
            "Process Code(s) + Run Rate: Max Rate must be greater than or equal to Min Rate.",
          );
      }

      return errors;
    },

    async restoreFromCriteria(criteria) {
      const matCriteria =
        criteria.MaterialCriteria || criteria.materialCriteria || [];
      const procCriteria =
        criteria.ProcessCriteria || criteria.processCriteria || [];
      const scope = criteria.ScopeFilters || criteria.scopeFilters || {};

      var qtyRangeRows = [];
      var codeAndQtyRows = [];
      var codeAndQtyCodes = [];

      for (const mc of matCriteria) {
        const type = mc.SearchType || mc.searchType;
        if (type === "CodeOnly") {
          this.materialSearch.codeOnly.active = true;
          this.materialSearch.codeOnly.codes =
            mc.MaterialCodes || mc.materialCodes || [];
        } else if (type === "QtyRangeOnly") {
          this.materialSearch.qtyRange.active = true;
          qtyRangeRows.push({
            minQty: mc.MinQty != null ? mc.MinQty : mc.minQty,
            maxQty: mc.MaxQty != null ? mc.MaxQty : mc.maxQty,
            uom: mc.UnitOfMeasure || mc.unitOfMeasure || null,
          });
        } else if (type === "CodeAndQty") {
          this.materialSearch.codeAndQty.active = true;
          if (codeAndQtyCodes.length === 0) {
            codeAndQtyCodes = mc.MaterialCodes || mc.materialCodes || [];
          }
          codeAndQtyRows.push({
            minQty: mc.MinQty != null ? mc.MinQty : mc.minQty,
            maxQty: mc.MaxQty != null ? mc.MaxQty : mc.maxQty,
            uom: mc.UnitOfMeasure || mc.unitOfMeasure || null,
          });
        } else if (type === "MaterialAttributes") {
          this.materialSearch.materialAttributes.active = true;
          const attrs = mc.MaterialAttributes || mc.materialAttributes || {};
          if (attrs.ProductCategory1Codes || attrs.productCategory1Codes) {
            this.materialSearch.materialAttributes.productCategory1Codes =
              attrs.ProductCategory1Codes || attrs.productCategory1Codes || [];
          }
          if (attrs.ProductCategory2Codes || attrs.productCategory2Codes) {
            this.materialSearch.materialAttributes.productCategory2Codes =
              attrs.ProductCategory2Codes || attrs.productCategory2Codes || [];
          }
          if (attrs.ProductCategory3Codes || attrs.productCategory3Codes) {
            this.materialSearch.materialAttributes.productCategory3Codes =
              attrs.ProductCategory3Codes || attrs.productCategory3Codes || [];
          }
          if (attrs.ProductCategory4Codes || attrs.productCategory4Codes) {
            this.materialSearch.materialAttributes.productCategory4Codes =
              attrs.ProductCategory4Codes || attrs.productCategory4Codes || [];
          }
          if (attrs.ProductTypeCodes || attrs.productTypeCodes) {
            this.materialSearch.materialAttributes.productTypeCodes =
              attrs.ProductTypeCodes || attrs.productTypeCodes || [];
          }
        }
      }

      if (qtyRangeRows.length > 0) {
        this.materialSearch.qtyRange.rows = qtyRangeRows;
      }
      if (codeAndQtyRows.length > 0) {
        this.materialSearch.codeAndQty.codes = codeAndQtyCodes;
        this.materialSearch.codeAndQty.rows = codeAndQtyRows;
      }

      for (const pc of procCriteria) {
        const type = pc.SearchType || pc.searchType;
        if (type === "CodeOnly") {
          this.processSearch.codeOnly.active = true;
          this.processSearch.codeOnly.codes =
            pc.ProcessCodes || pc.processCodes || [];
        } else if (type === "RunRateRangeOnly") {
          this.processSearch.rateRange.active = true;
          this.processSearch.rateRange.minRate =
            pc.MinRunRate != null ? pc.MinRunRate : pc.minRunRate;
          this.processSearch.rateRange.maxRate =
            pc.MaxRunRate != null ? pc.MaxRunRate : pc.maxRunRate;
        } else if (type === "CodeAndRunRate") {
          this.processSearch.codeAndRate.active = true;
          this.processSearch.codeAndRate.codes =
            pc.ProcessCodes || pc.processCodes || [];
          this.processSearch.codeAndRate.minRate =
            pc.MinRunRate != null ? pc.MinRunRate : pc.minRunRate;
          this.processSearch.codeAndRate.maxRate =
            pc.MaxRunRate != null ? pc.MaxRunRate : pc.maxRunRate;
        }
      }

      if (scope.BomTypeCode != null)
        this.scopeFilters.billType = scope.BomTypeCode;
      if (scope.BomStatus != null)
        this.scopeFilters.bomStatus = scope.BomStatus;
      if (scope.BranchCode != null)
        this.scopeFilters.branchCode = scope.BranchCode;
      if (scope.DivisionCode != null)
        this.scopeFilters.divisionCode = scope.DivisionCode;
      if (scope.ProductionUM != null)
        this.scopeFilters.productionUM = scope.ProductionUM;
      if (scope.ProductTypeCode != null)
        this.scopeFilters.productType = scope.ProductTypeCode;
      if (scope.ProductCategory1Code != null)
        this.scopeFilters.productCategory1 = scope.ProductCategory1Code;
      if (scope.ProductCategory2Code != null)
        this.scopeFilters.productCategory2 = scope.ProductCategory2Code;
      if (scope.ProductCategory3Code != null)
        this.scopeFilters.productCategory3 = scope.ProductCategory3Code;
      if (scope.ProductCategory4Code != null)
        this.scopeFilters.productCategory4 = scope.ProductCategory4Code;
      const guidsToResolve = [];
      for (let i = 1; i <= 20; i++) {
        const n = String(i).padStart(2, "0");
        const guid = scope[`Attribute${n}`];
        const code = scope[`AttributeCode${n}`];
        if (guid != null) this.attrGuidMap[`productAttribute${n}`] = guid;
        if (code != null) {
          this.attrScopeFilters[`productAttribute${n}`] = code;
        } else if (guid != null) {
          guidsToResolve.push({ n, guid });
        }
      }

      if (guidsToResolve.length > 0) {
        await Promise.all(
          guidsToResolve.map(async ({ n, guid }) => {
            try {
              const res = await ApiService.oDataGet({
                url: `api/ProductAttribute${n}`,
                top: "1",
                filter: `RecordId eq ${guid}`,
                select: "Code",
              });
              const item = res.data && res.data.value && res.data.value[0];
              if (item) {
                this.attrScopeFilters[`productAttribute${n}`] = item.Code;
              }
            } catch (_) {
              // skip if attribute lookup fails
            }
          }),
        );
      }
      this.$nextTick(() => {
        this.lastSearchedSnapshot = this.buildCriteriaSnapshot();
      });
    },

    resetFilters() {
      this.codeOnlySelectValue = null;
      this.codeAndQtySelectValue = null;
      this.procCodeOnlySelectValue = null;
      this.procCodeAndRateSelectValue = null;
      this.materialSearch = {
        codeOnly: { active: false, codes: [] },
        qtyRange: {
          active: false,
          rows: [{ minQty: null, maxQty: null, uom: null }],
        },
        codeAndQty: {
          active: false,
          codes: [],
          rows: [{ minQty: null, maxQty: null, uom: null }],
        },
        materialAttributes: {
          active: false,
          productCategory1Codes: [],
          productCategory2Codes: [],
          productCategory3Codes: [],
          productCategory4Codes: [],
          productTypeCodes: [],
        },
      };
      this.processSearch = {
        codeOnly: { active: false, codes: [] },
        rateRange: { active: false, minRate: null, maxRate: null },
        codeAndRate: {
          active: false,
          codes: [],
          minRate: null,
          maxRate: null,
        },
      };
      this.scopeFilters = {
        billType: null,
        bomStatus: null,
        branchCode: null,
        divisionCode: null,
        productionUM: null,
        conversionFactor: null,
        productType: null,
        productCategory1: null,
        productCategory2: null,
        productCategory3: null,
        productCategory4: null,
      };
      for (let i = 1; i <= 20; i++) {
        const key = `productAttribute${String(i).padStart(2, "0")}`;
        this.attrScopeFilters[key] = null;
        this.attrGuidMap[key] = null;
      }
      this.attributesPanelExpanded = false;
      this.searchAttempted = false;
    },

    // ─── Search execution (UI-04.5 — wired when API is ready) ─────────────────

    async executeSearch() {
      this.searchAttempted = true;

      const errors = this.collectSearchValidationErrors();
      if (!this.canSearch || errors.length > 0) {
        await this.$alert(errors.join("</br>"), "Validation Error");
        return;
      }

      this.showLoading(true);
      try {
        this.searching = true;
        const payload = this.buildSearchPayload();
        const res = await BomBulkUpdateService.search(payload);
        const matched = res.data.MatchedBoms || [];
        if (matched.length === 0) {
          await this.$alert(
            "No BOMs matched the search criteria. Please adjust your search and try again.",
            "No Results",
          );
          return;
        }
        this.lastSearchedSnapshot = JSON.stringify(payload.SearchCriteria);
        const sc = payload.SearchCriteria;
        this.$emit("search-complete", {
          matchedBomList: matched,
          searchCriteria: sc,
          hasMaterialCriteria: (sc.MaterialCriteria || []).length > 0,
          hasProcessCriteria: (sc.ProcessCriteria || []).length > 0,
        });
      } catch (err) {
        this.showLoading(false);
        this.$notify("Search failed. Please try again.", "error");
      } finally {
        this.searching = false;
        this.showLoading(false);
      }
    },

    buildSearchPayload() {
      const MaterialCriteria = [];
      const ProcessCriteria = [];
      const m = this.materialSearch;
      const p = this.processSearch;
      var self = this;

      if (m.codeOnly.active && m.codeOnly.codes.length > 0) {
        MaterialCriteria.push({
          SearchType: "CodeOnly",
          MaterialCodes: m.codeOnly.codes,
        });
      }
      if (m.qtyRange.active) {
        m.qtyRange.rows.forEach(function (row) {
          if (self.isValidRangeRow(row)) {
            MaterialCriteria.push({
              SearchType: "QtyRangeOnly",
              MinQty: row.minQty,
              MaxQty: row.maxQty,
              UnitOfMeasure: row.uom,
            });
          }
        });
      }
      if (m.codeAndQty.active && m.codeAndQty.codes.length > 0) {
        m.codeAndQty.rows.forEach(function (row) {
          if (self.isValidRangeRow(row)) {
            MaterialCriteria.push({
              SearchType: "CodeAndQty",
              MaterialCodes: m.codeAndQty.codes,
              MinQty: row.minQty,
              MaxQty: row.maxQty,
              UnitOfMeasure: row.uom,
            });
          }
        });
      }
      if (m.materialAttributes.active && this.isMaterialAttributesValid) {
        var a = m.materialAttributes;
        var attrFilter = {};

        if (a.productCategory1Codes && a.productCategory1Codes.length) {
          attrFilter.ProductCategory1Codes = a.productCategory1Codes;
        }
        if (a.productCategory2Codes && a.productCategory2Codes.length) {
          attrFilter.ProductCategory2Codes = a.productCategory2Codes;
        }
        if (a.productCategory3Codes && a.productCategory3Codes.length) {
          attrFilter.ProductCategory3Codes = a.productCategory3Codes;
        }
        if (a.productCategory4Codes && a.productCategory4Codes.length) {
          attrFilter.ProductCategory4Codes = a.productCategory4Codes;
        }
        if (a.productTypeCodes && a.productTypeCodes.length) {
          attrFilter.ProductTypeCodes = a.productTypeCodes;
        }

        MaterialCriteria.push({
          SearchType: "MaterialAttributes",
          MaterialAttributes: attrFilter,
        });
      }
      if (p.codeOnly.active && p.codeOnly.codes.length > 0) {
        ProcessCriteria.push({
          SearchType: "CodeOnly",
          ProcessCodes: p.codeOnly.codes,
        });
      }
      if (p.rateRange.active) {
        ProcessCriteria.push({
          SearchType: "RunRateRangeOnly",
          MinRunRate: p.rateRange.minRate,
          MaxRunRate: p.rateRange.maxRate,
        });
      }
      if (p.codeAndRate.active && p.codeAndRate.codes.length > 0) {
        ProcessCriteria.push({
          SearchType: "CodeAndRunRate",
          ProcessCodes: p.codeAndRate.codes,
          MinRunRate: p.codeAndRate.minRate,
          MaxRunRate: p.codeAndRate.maxRate,
        });
      }

      const s = this.scopeFilters;
      const ScopeFilters = {};
      if (s.billType != null) ScopeFilters.BomTypeCode = s.billType;
      if (s.bomStatus != null) ScopeFilters.BomStatus = s.bomStatus;
      if (s.branchCode != null) ScopeFilters.BranchCode = s.branchCode;
      if (s.divisionCode != null) ScopeFilters.DivisionCode = s.divisionCode;
      if (s.productionUM != null) ScopeFilters.ProductionUM = s.productionUM;
      if (s.productType != null) ScopeFilters.ProductTypeCode = s.productType;
      if (s.productCategory1 != null)
        ScopeFilters.ProductCategory1Code = s.productCategory1;
      if (s.productCategory2 != null)
        ScopeFilters.ProductCategory2Code = s.productCategory2;
      if (s.productCategory3 != null)
        ScopeFilters.ProductCategory3Code = s.productCategory3;
      if (s.productCategory4 != null)
        ScopeFilters.ProductCategory4Code = s.productCategory4;
      for (let i = 1; i <= 20; i++) {
        const stateKey = `productAttribute${String(i).padStart(2, "0")}`;
        const n = String(i).padStart(2, "0");
        const guid = this.attrGuidMap[stateKey];
        const code = this.attrScopeFilters[stateKey];
        if (guid != null) ScopeFilters[`Attribute${n}`] = guid;
        if (code != null) ScopeFilters[`AttributeCode${n}`] = code;
      }

      return {
        SearchCriteria: { MaterialCriteria, ProcessCriteria, ScopeFilters },
      };
    },
  },
};
</script>

<style lang="scss">
.material-search-dropdown {
  .dx-list-item-content::before {
    content: unset !important;
  }

  .dx-list-item-content {
    font-size: 13px;
    padding: 10px 11px 9px;
  }
}
.gc-bom-bulk-update-step-search {
  // ── Two-column layout ──────────────────────────────────────────────────────
  .search-page-content {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .search-main-col {
    flex: 1;
    min-width: 0;
  }

  .search-filter-col {
    width: 300px;
    flex-shrink: 0;
  }

  // ── Card ──────────────────────────────────────────────────────────────────
  .search-card {
    background: #fff;
    border: 1px solid #d0d5dd;
  }

  .search-card-header {
    padding: 12px 16px;
    border-bottom: 1px solid #eaecf0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .search-card-title {
    font-size: 14px;
    font-weight: 700;
    color: #101828;
  }

  .search-card-body {
    padding: 16px;
  }

  // ── Tab bar ───────────────────────────────────────────────────────────────
  .search-tabs {
    display: flex;
    border-bottom: 1px solid #eaecf0;
    margin-bottom: 16px;
  }

  .search-tab {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 40px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 500;
    color: #98a2b3;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    user-select: none;
    white-space: nowrap;

    &:hover:not(.active) {
      color: #101828;
    }

    &.active {
      color: #03a9f4;
      border-bottom-color: #03a9f4;
    }

    &.has-criteria:not(.active) {
      color: #344054;
    }
  }

  .tab-active-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: #17b26a;
    color: #fff;
    font-size: 10px;
    font-weight: 600;
    line-height: 1;
  }

  // ── Hint text ─────────────────────────────────────────────────────────────
  .search-hint-text {
    font-size: 13px;
    color: #98a2b3;
    margin-bottom: 14px;
    line-height: 1.5;
  }

  .search-tab-content {
    min-height: 60px;
  }

  // ── Search type blocks ────────────────────────────────────────────────────
  .stb {
    border: 1px solid #d0d5dd;
    padding: 12px;
    margin-bottom: 8px;
    background: #f9fafb;
    transition: border-color 0.12s, background 0.12s;

    &.stb-on {
      border-color: #03a9f4;
      background: #f0faff;
    }
  }

  .stb-hdr {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .stb-chk {
    width: 16px;
    height: 16px;
    cursor: pointer;
    flex-shrink: 0;
  }

  .stb-lbl {
    font-size: 14px;
    font-weight: 500;
    color: #101828;
    cursor: pointer;
    user-select: none;
  }

  .stb-desc {
    font-size: 12px;
    color: #98a2b3;
    margin-left: 24px;
    margin-top: 3px;
  }

  .stb-body {
    padding-left: 24px;
    margin-top: 10px;
  }

  // ── Tags ──────────────────────────────────────────────────────────────────
  .tag-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }

  .stb-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #fff;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    padding: 2px 4px 2px 9px;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
  }

  .stb-tag-x {
    cursor: pointer;
    font-size: 12px;
    color: #98a2b3;
    padding: 2px;
    line-height: 1;

    &:hover {
      color: #101828;
    }
  }

  // ── Range row ─────────────────────────────────────────────────────────────
  .stb-range-row {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .stb-range-field {
    // flex: 1;
    // min-width: 80px;
    width: 125px;
  }

  .stb-range-field-sm {
    flex: 0 0 90px;
  }

  .stb-field-label {
    font-size: 12px;
    font-weight: 500;
    color: #344054;
    margin-bottom: 4px;
    display: block;
  }

  .stb-error {
    font-size: 12px;
    color: #d92d20;
    margin-top: 6px;
    display: block;
  }

  // ── Divider / scope section ───────────────────────────────────────────────
  .search-divider {
    height: 1px;
    background: #eaecf0;
    margin: 14px 0;
  }

  .scope-section-hdr {
    font-size: 12px;
    font-weight: 500;
    color: #98a2b3;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
  }

  // ── Scope form wrapper ────────────────────────────────────────────────
  .scope-form-wrap {
    margin-bottom: 4px;
  }

  .scope-form-readonly {
    pointer-events: none;
    opacity: 0.6;
  }

  // ── Attributes collapsible ────────────────────────────────────────────
  .scope-attrs-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 0;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid #eaecf0;
    margin-bottom: 0;

    &:hover {
      color: #101828;
    }
  }

  .scope-attrs-caret {
    margin-left: auto;
    font-size: 11px;
    color: #98a2b3;
  }

  .scope-attrs-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #03a9f4;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }

  .scope-attrs-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px 12px;
    margin-top: 8px;
    margin-bottom: 12px;
  }

  .scope-attr-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  // ── Material Attributes search type ────────────────────────────────────────
  .attribute-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 16px;
    margin-bottom: 12px;
  }

  .attribute-field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      font-size: 12px;
      font-weight: 500;
      color: #344054;
    }

    // DxTagBox styling
    .dx-tagbox {
      width: 100%;

      .dx-textbox-container {
        border: 1px solid #d0d5dd;
        border-radius: 4px;
        background: #fff;
        min-height: 32px;
        padding: 4px 8px;

        &:hover {
          border-color: #98a2b3;
        }

        &.dx-state-focused {
          border-color: #03a9f4;
          box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.1);
        }
      }

      .dx-tag {
        background: #ffffff;
        border: 1px solid #d0d5dd;
        color: #000000;
        border-radius: 4px;
        padding: 3px 6px;
        font-size: 12px;
        margin: 2px 2px;

        .dx-tag-content {
          background-color: white !important;

          > span {
            font-weight: 500;
            color: black !important;
          }
        }

        &:hover {
          background: #e7eef7;
        }

        .dx-tag-remove-button {
          color: #98a2b3;
          margin-left: 4px;

          &:hover {
            color: #d92d20;
          }

          cursor: pointer;
          font-size: 12px;
          color: #98a2b3;
          padding: 2px;
          line-height: 1;
        }
      }

      .dx-texteditor-input {
        font-size: 13px;
        color: #344054;

        &::placeholder {
          color: #98a2b3;
        }
      }

      .dx-placeholder {
        color: #98a2b3;
      }
    }
  }

  // ── Search action ─────────────────────────────────────────────────────────
  .search-action-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 16px;
  }

  .search-error-hint {
    font-size: 12px;
    color: #d92d20;
  }

  // ── Active filters sidebar (read-only mode) ──────────────────────────────
  .filters-readonly .chip-x {
    display: none;
  }

  // ── Active filters sidebar ────────────────────────────────────────────────
  .filter-section-label {
    font-size: 11px;
    font-weight: 500;
    color: #98a2b3;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 14px;
    margin-bottom: 6px;
    display: block;

    &:first-child {
      margin-top: 0;
    }
  }

  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    padding: 2px 4px 2px 9px;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    margin: 3px 3px 0 0;
  }

  .chip-x {
    cursor: pointer;
    font-size: 12px;
    color: #98a2b3;
    padding: 2px;

    &:hover {
      color: #101828;
    }
  }

  .search-no-filters {
    font-size: 13px;
    color: #98a2b3;
  }

  // ── Configure tip card ────────────────────────────────────────────────────
  .cfg-tip-card {
    margin-top: 10px;
  }

  .cfg-tip-body {
    font-size: 13px;
    color: #344054;
    line-height: 1.6;
  }

  // ── Range row styling (shared with material-config replacement-row) ────────
  .replacement-header {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 4px;

    .rt-col-code {
      width: 125px;
      // flex: 1;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-del {
      width: 28px;
    }
  }

  .replacement-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 6px;

    .rt-del-btn {
      flex-shrink: 0;
      margin-top: 6px;
      color: #6c757d;

      &:hover {
        color: #dc3545;
      }

      &:disabled {
        color: #d1d5db;
        cursor: not-allowed;
      }
    }
  }

  .rt-field {
    width: 125px;
    // flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .dx-selectbox,
    .dx-numberbox {
      width: 100%;
    }

    &.rt-field-narrow {
      flex: 0 0 120px;
    }
  }

  .add-row-btn {
    margin-top: 4px;
    font-size: 13px;
  }

  .param-error {
    display: block;
    font-size: 12px;
    color: #dc3545;
    margin-top: 2px;
  }
}
</style>
